import React, { useEffect, useState, useMemo } from "react";
import { toastError, toastSuccess } from "../../utils";
import * as actions from "./actions";
import * as commonActions from "../common/actions";
import {
  Grid,
  Box,
  Paper,
  TextField,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Fade,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BadgeIcon from "@mui/icons-material/Badge";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import TagIcon from "@mui/icons-material/Tag";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import DownloadIcon from "@mui/icons-material/Download";
import { Link } from "react-router-dom";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import SEO from "../../components/SEO";
import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import FronicLogo from "../../assets/img/Fronic_logo_black.png";

if (pdfMake.vfs === undefined) {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;
}

const PageContainer = styled(Box)({
  minHeight: 'calc(100vh - 64px)',
  background: 'linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)',
  padding: '32px 24px',
});

const PageHeader = styled(Box)({
  marginBottom: '32px',
  textAlign: 'left',
  '& h1': {
    fontSize: '2rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontFamily: "'DM Sans', sans-serif",
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
  },
});

const InfoCard = styled(Paper)({
  borderRadius: '16px',
  padding: '32px',
  background: 'white',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  marginBottom: '24px',
});

const SectionTitle = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#202124',
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  '& svg': {
    color: '#1a73e8',
  },
});

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px 0',
  borderBottom: '1px solid #f0f0f0',
  '&:last-child': {
    borderBottom: 'none',
  },
});

const InfoLabel = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#5f6368',
  minWidth: '140px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const InfoValue = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '1rem',
  fontWeight: 500,
  color: '#202124',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
});

const EditButton = styled(IconButton)({
  color: '#1a73e8',
  padding: '8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#e3f2fd',
    transform: 'scale(1.1)',
  },
});

const ActionButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
  color: 'white',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 32px',
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #1557b0 0%, #00a589 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(26, 115, 232, 0.4)',
  },
  '&:disabled': {
    background: '#e0e0e0',
    color: '#9e9e9e',
    boxShadow: 'none',
  },
});

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: '8px',
    maxWidth: '500px',
    width: '100%',
    backgroundColor: '#fafafa',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '1.5rem',
  fontWeight: 700,
  color: '#202124',
  padding: '24px 24px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#fafafa',
});

const StyledDialogContent = styled(DialogContent)({
  padding: '24px',
  background: '#fafafa',
});

const StyledDialogActions = styled(DialogActions)({
  padding: '16px 24px 24px',
  gap: '12px',
  background: '#fafafa',
});

const StyledTextField = styled(TextField)({
  fontFamily: "'DM Sans', sans-serif",
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    fontFamily: "'DM Sans', sans-serif",
    '& fieldset': {
      borderColor: '#e0e0e0',
      borderWidth: '2px',
    },
    '&:hover fieldset': {
      borderColor: '#1a73e8',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1a73e8',
    },
  },
  '& .MuiInputBase-input': {
    color: '#202124',
    fontFamily: "'DM Sans', sans-serif",
  },
  '& .MuiInputLabel-root': {
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 600,
    color: '#5f6368',
    '&.Mui-focused': {
      color: '#1a73e8',
    },
  },
});

const FieldLabel = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#202124',
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
});

const NoteText = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.875rem',
  color: '#5f6368',
  textAlign: 'center',
  marginTop: '16px',
  '& a': {
    color: '#1a73e8',
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

const StyledTableContainer = styled(TableContainer)({
  borderRadius: "16px",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  backgroundColor: "white",
  overflow: "hidden",
  "& .MuiTable-root": {
    fontFamily: "'DM Sans', sans-serif",
    backgroundColor: "white",
  },
  "& .MuiTableHead-root": {
    background: "linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)",
    "& .MuiTableCell-head": {
      color: "white",
      fontWeight: 700,
      fontSize: "0.9375rem",
      fontFamily: "'DM Sans', sans-serif",
      padding: "16px",
    },
  },
  "& .MuiTableBody-root": {
    backgroundColor: "white",
    "& .MuiTableRow-root": {
      backgroundColor: "white !important",
      transition: "background-color 0.2s ease",
      "&:nth-of-type(odd)": {
        backgroundColor: "#f9f9f9 !important",
      },
      "&:hover": {
        backgroundColor: "#f0f7ff !important",
      },
    },
  },
  "& .MuiTableCell-body": {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "0.875rem",
    color: "#202124",
    padding: "16px",
    borderBottom: "1px solid #f0f0f0",
  },
});

const StatusChip = styled(Chip)(({ statustype }) => {
  const getStatusColor = () => {
    switch (statustype) {
      case "closed":
        return { bg: "#4caf50", color: "#ffffff" };
      case "open":
      default:
        return { bg: "#ff9800", color: "#ffffff" };
    }
  };

  const colors = getStatusColor();

  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
    fontSize: "0.75rem",
    height: "28px",
    fontFamily: "'DM Sans', sans-serif",
  };
});

export default function Withdrawals(props) {
  const [user, setUser] = useState({});
  const [bankDetails, setBankDetails] = useState({
    bank_name: "",
    holder_name: "",
    account_number: "",
    ifsc_code: "",
    branch_name: "",
    pan: "",
    other_details: "",
  });
  const [tempBankDetails, setTempBankDetails] = useState({
    bank_name: "",
    holder_name: "",
    account_number: "",
    ifsc_code: "",
    branch_name: "",
    pan: "",
    other_details: "",
  });
  const [showBank, setShowBank] = useState(false);
  const [addBankModal, setAddBankModal] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);

  const isBankDetailsFilled = useMemo(() => {
    const requiredFields = ['bank_name', 'holder_name', 'account_number', 'ifsc_code', 'branch_name', 'pan'];
    return requiredFields.every(field =>
      bankDetails[field] && String(bankDetails[field]).trim() !== ""
    );
  }, [bankDetails]);

  const payoutTotals = useMemo(() => {
    if (!Array.isArray(payoutHistory) || payoutHistory.length === 0) {
      return {
        totalUSD: 0,
        totalGrossINR: 0,
        totalTDS: 0,
        totalNetINR: 0,
      };
    }

    return payoutHistory
      .filter(item => item.status === "closed")
      .reduce((acc, item) => {
        const usd = parseFloat(item.amount) || 0;
        const grossINR = parseFloat(item.gross_amt) || 0;
        const tds = parseFloat(item.tds) || 0;
        const netINR = parseFloat(item.email_amt) || 0;

        return {
          totalUSD: acc.totalUSD + usd,
          totalGrossINR: acc.totalGrossINR + grossINR,
          totalTDS: acc.totalTDS + tds,
          totalNetINR: acc.totalNetINR + netINR,
        };
      }, {
        totalUSD: 0,
        totalGrossINR: 0,
        totalTDS: 0,
        totalNetINR: 0,
      });
  }, [payoutHistory]);

  useEffect(() => {
    getAllUserData();
    onGetUserBankDetails();
    getPayoutHistoryData();

    // Track user activity on withdrawals page
    commonActions.trackUserActivity({
      page: '/withdrawals',
      timestamp: new Date().toISOString()
    }).catch((err) => {
      console.error('Error tracking activity:', err);
    });
  }, []);

  const getAllUserData = () => {
    props.setLoader(true);
    actions
      .getUser()
      .then((res) => {
        props.setLoader(false);
        setUser(res.body);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err.message);
      });
  };

  const onGetUserBankDetails = () => {
    actions
      .getUserBankDetails()
      .then((res) => {
        setBankDetails(res.body);
      })
      .catch((err) => {
        toastError(err.message);
      });
  };

  const getPayoutHistoryData = () => {
    actions
      .getPayoutHistory()
      .then((res) => {
        setPayoutHistory(Array.isArray(res.body) ? res.body : []);
      })
      .catch((err) => {
        toastError(err.message);
      });
  };

  const onClickSubmitBank = () => {
    const requiredFields = ['bank_name', 'holder_name', 'account_number', 'ifsc_code', 'branch_name', 'pan'];
    const isAllFieldsFilled = requiredFields.every(field =>
      tempBankDetails[field] && String(tempBankDetails[field]).trim() !== ""
    );

    if (!isAllFieldsFilled) {
      return toastError("All fields are required");
    }

    props.setLoader(true);
    actions
      .addBankDetails(tempBankDetails)
      .then((res) => {
        props.setLoader(false);
        setAddBankModal(false);
        toastSuccess(res.message);
        onGetUserBankDetails();
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err.message);
      });
  };

  const requestForPayoutUser = () => {
    props.setLoader(true);
    actions
      .withdrawMoney()
      .then((res) => {
        props.setLoader(false);
        toastSuccess(res.message);
        setConfirmOpen(false);
        getPayoutHistoryData();
      })
      .catch((err) => {
        setConfirmOpen(false);
        props.setLoader(false);
        toastError(err.message);
      });
  };

  const {
    revenue = "",
    conversionRate = 0,
  } = user || {};

  const {
    bank_name = "",
    ifsc_code = "",
    account_number = "",
    holder_name = "",
    other_details = "",
    branch_name = "",
    pan = "",
  } = bankDetails || {};

  const exportToPDF = () => {
    if (!Array.isArray(payoutHistory) || payoutHistory.length === 0) {
      toastError("No withdrawal history to export.");
      return;
    }

    try {
      // Convert logo to base64
      const getBase64ImageFromURL = (url) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.setAttribute("crossOrigin", "anonymous");
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL("image/png");
            resolve(dataURL);
          };
          img.onerror = (error) => reject(error);
          img.src = url;
        });
      };

      // Prepare table body
      const tableBody = payoutHistory.map((item, index) => {
        const statusLabel = item.status === "closed" ? "PAID" : "PENDING";
        return [
          { text: (index + 1).toString(), alignment: 'center' },
          { text: `$ ${item.amount || "0.00"}`, bold: true },
          item.gross_amt ? `₹ ${item.gross_amt}` : "N/A",
          item.tds ? `₹ ${item.tds}` : "N/A",
          { text: `₹ ${item.email_amt || "0.00"}`, bold: true },
          item.created_at ? moment(item.created_at).format("MMM DD, YYYY") : "N/A",
          item.utr || "N/A",
          item.ref || "N/A",
          item.settled_on && item.settled_on !== "N/A"
            ? moment(item.settled_on, "DD/MM/YYYY [at] HH:mm").format("MMM DD, YYYY")
            : "N/A",
          {
            text: statusLabel,
            alignment: 'center',
            color: statusLabel === "PAID" ? '#4CAF50' : '#FF9800',
            bold: true
          },
        ];
      });

      // Add totals row if there are paid withdrawals
      if (payoutTotals.totalUSD > 0 || payoutTotals.totalNetINR > 0) {
        tableBody.push([
          { text: "TOTAL", bold: true, color: '#1a73e8', fillColor: '#f5f5f5' },
          { text: `$ ${payoutTotals.totalUSD.toFixed(2)}`, bold: true, color: '#1a73e8', fillColor: '#f5f5f5' },
          { text: `₹ ${payoutTotals.totalGrossINR.toFixed(2)}`, bold: true, color: '#1a73e8', fillColor: '#f5f5f5' },
          { text: `₹ ${payoutTotals.totalTDS.toFixed(2)}`, bold: true, color: '#1a73e8', fillColor: '#f5f5f5' },
          { text: `₹ ${payoutTotals.totalNetINR.toFixed(2)}`, bold: true, color: '#1a73e8', fillColor: '#f5f5f5' },
          { text: "", fillColor: '#f5f5f5' },
          { text: "", fillColor: '#f5f5f5' },
          { text: "", fillColor: '#f5f5f5' },
          { text: "", fillColor: '#f5f5f5' },
          { text: "", fillColor: '#f5f5f5' },
        ]);
      }

      // Load logo and generate PDF
      getBase64ImageFromURL(FronicLogo)
        .then((logoDataURL) => {
          const docDefinition = {
            pageSize: 'A4',
            pageOrientation: 'landscape',
            pageMargins: [40, 60, 40, 60],
            header: function(currentPage, pageCount) {
              return {
                columns: [
                  {
                    text: '',
                    width: '*'
                  },
                  {
                    image: logoDataURL,
                    width: 80,
                    alignment: 'right',
                    margin: [0, 10, 40, 0]
                  }
                ]
              };
            },
            content: [
              {
                text: 'Withdrawal History Report',
                style: 'header',
                color: '#1a73e8',
                margin: [0, 0, 0, 5]
              },
              {
                text: `Generated on: ${moment().format("MMMM DD, YYYY [at] HH:mm")}`,
                style: 'subheader',
                color: '#5f6368'
              },
              {
                text: '(History from September 1, 2025 onwards)',
                style: 'subheader',
                color: '#5f6368',
                margin: [0, 0, 0, 20]
              },
              {
                table: {
                  headerRows: 1,
                  widths: [30, 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 50],
                  body: [
                    [
                      { text: 'No.', style: 'tableHeader', alignment: 'center' },
                      { text: 'Amount (USD)', style: 'tableHeader' },
                      { text: 'Gross Amount (INR)', style: 'tableHeader' },
                      { text: 'TDS Amount (INR)', style: 'tableHeader' },
                      { text: 'Net Payable Amount (INR)', style: 'tableHeader' },
                      { text: 'Requested On', style: 'tableHeader' },
                      { text: 'UTR', style: 'tableHeader' },
                      { text: 'Reference', style: 'tableHeader' },
                      { text: 'Transferred On', style: 'tableHeader' },
                      { text: 'Status', style: 'tableHeader', alignment: 'center' }
                    ],
                    ...tableBody
                  ]
                },
                layout: {
                  fillColor: function (rowIndex) {
                    if (rowIndex === 0) return '#1a73e8';
                    return (rowIndex % 2 === 0) ? '#f9f9f9' : null;
                  },
                  hLineWidth: function () { return 0.5; },
                  vLineWidth: function () { return 0.5; },
                  hLineColor: function () { return '#e0e0e0'; },
                  vLineColor: function () { return '#e0e0e0'; }
                }
              }
            ],
            footer: function(currentPage, pageCount) {
              return {
                text: 'This document is auto-generated and contains confidential information.',
                alignment: 'center',
                fontSize: 8,
                color: '#5f6368',
                margin: [0, 20, 0, 0]
              };
            },
            styles: {
              header: {
                fontSize: 20,
                bold: true
              },
              subheader: {
                fontSize: 10
              },
              tableHeader: {
                bold: true,
                fontSize: 9,
                color: 'white'
              }
            },
            defaultStyle: {
              fontSize: 8
            }
          };

          pdfMake.createPdf(docDefinition).download(`Fronic_Media_Withdrawal_History_${moment().format("YYYY-MM-DD")}.pdf`);
          toastSuccess("PDF exported successfully!");
        })
        .catch((error) => {
          console.error("Error loading logo:", error);
          // Generate PDF without logo if image loading fails
          const docDefinition = {
            pageSize: 'A4',
            pageOrientation: 'landscape',
            pageMargins: [40, 60, 40, 60],
            content: [
              {
                text: 'Withdrawal History Report',
                style: 'header',
                color: '#1a73e8',
                margin: [0, 0, 0, 5]
              },
              {
                text: `Generated on: ${moment().format("MMMM DD, YYYY [at] HH:mm")}`,
                style: 'subheader',
                color: '#5f6368'
              },
              {
                text: '(History from September 1, 2025 onwards)',
                style: 'subheader',
                color: '#5f6368',
                margin: [0, 0, 0, 20]
              },
              {
                table: {
                  headerRows: 1,
                  widths: [30, 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 50],
                  body: [
                    [
                      { text: 'No.', style: 'tableHeader', alignment: 'center' },
                      { text: 'Amount (USD)', style: 'tableHeader' },
                      { text: 'Gross Amount (INR)', style: 'tableHeader' },
                      { text: 'TDS Amount (INR)', style: 'tableHeader' },
                      { text: 'Net Payable Amount (INR)', style: 'tableHeader' },
                      { text: 'Requested On', style: 'tableHeader' },
                      { text: 'UTR', style: 'tableHeader' },
                      { text: 'Reference', style: 'tableHeader' },
                      { text: 'Transferred On', style: 'tableHeader' },
                      { text: 'Status', style: 'tableHeader', alignment: 'center' }
                    ],
                    ...tableBody
                  ]
                },
                layout: {
                  fillColor: function (rowIndex) {
                    if (rowIndex === 0) return '#1a73e8';
                    return (rowIndex % 2 === 0) ? '#f9f9f9' : null;
                  },
                  hLineWidth: function () { return 0.5; },
                  vLineWidth: function () { return 0.5; },
                  hLineColor: function () { return '#e0e0e0'; },
                  vLineColor: function () { return '#e0e0e0'; }
                }
              }
            ],
            footer: function(currentPage, pageCount) {
              return {
                text: 'This document is auto-generated and contains confidential information.',
                alignment: 'center',
                fontSize: 8,
                color: '#5f6368',
                margin: [0, 20, 0, 0]
              };
            },
            styles: {
              header: {
                fontSize: 20,
                bold: true
              },
              subheader: {
                fontSize: 10
              },
              tableHeader: {
                bold: true,
                fontSize: 9,
                color: 'white'
              }
            },
            defaultStyle: {
              fontSize: 8
            }
          };

          pdfMake.createPdf(docDefinition).download(`Fronic_Media_Withdrawal_History_${moment().format("YYYY-MM-DD")}.pdf`);
          toastSuccess("PDF exported successfully!");
        });
    } catch (error) {
      console.error("Error generating PDF:", error);
      console.error("Error details:", error.message, error.stack);
      toastError(`Failed to export PDF: ${error.message || "Please try again."}`);
    }
  };

  return (
    <PageContainer>
      <SEO
        title="Withdraw Funds"
        description="Manage your bank details and request payouts."
      />
      <Fade in timeout={800}>
        <Box>
          <PageHeader>
            <Typography variant="h1">
              <AccountBalanceWalletIcon sx={{ fontSize: 36, color: '#1a73e8' }} />
              Withdraw Funds
            </Typography>
          </PageHeader>

          {/* Revenue Display */}
          <Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
            {/* USD Card */}
            <Grid item xs={12} sm={6} md={5} lg={4}>
              <InfoCard sx={{
                background: 'linear-gradient(135deg, #1a73e8 0%, #1557b0 100%)',
                color: 'white',
                textAlign: 'center',
                py: 2.5,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '"$"',
                  position: 'absolute',
                  fontSize: '150px',
                  opacity: 0.05,
                  fontWeight: 700,
                  top: '-30px',
                  right: '-20px',
                  fontFamily: "'DM Sans', sans-serif",
                }
              }}>
                <Typography sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  opacity: 0.85,
                  mb: 1,
                  fontFamily: "'DM Sans', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Available Balance in USD
                </Typography>
                <Typography sx={{
                  fontSize: '2.25rem',
                  fontWeight: 700,
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '-1px'
                }}>
                  ${revenue || "0.00"}
                </Typography>
              </InfoCard>
            </Grid>

            {/* INR Card */}
            <Grid item xs={12} sm={6} md={5} lg={4}>
              <InfoCard sx={{
                background: 'linear-gradient(135deg, #00bfa5 0%, #00a589 100%)',
                color: 'white',
                textAlign: 'center',
                py: 2.5,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '"₹"',
                  position: 'absolute',
                  fontSize: '150px',
                  opacity: 0.05,
                  fontWeight: 700,
                  top: '-30px',
                  right: '-20px',
                  fontFamily: "'DM Sans', sans-serif",
                }
              }}>
                <Typography sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  opacity: 0.85,
                  mb: 1,
                  fontFamily: "'DM Sans', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Available Balance in INR
                </Typography>
                <Typography sx={{
                  fontSize: '2.25rem',
                  fontWeight: 700,
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '-1px'
                }}>
                  ₹{conversionRate && revenue ? (parseFloat(revenue) * parseFloat(conversionRate)).toFixed(2) : "0.00"}
                </Typography>
              </InfoCard>
            </Grid>

          </Grid>

          {/* Currency Clarification Message */}
          <Box sx={{
            textAlign: 'center',
            mb: 3,
            mx: 'auto',
            maxWidth: '800px'
          }}>
            <Paper sx={{
              background: 'linear-gradient(135deg, #e3f2fd 0%, #e0f7fa 100%)',
              borderRadius: '12px',
              padding: '16px 24px',
              border: '2px solid #b3e5fc',
              boxShadow: '0 2px 8px rgba(26, 115, 232, 0.1)',
            }}>
              <Typography sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '0.875rem',
                color: '#1565c0',
                fontWeight: 500,
                lineHeight: 1.6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}>
                <Box component="span" sx={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                }}>
                  ℹ️
                </Box>
                <Box component="span">
                  <strong>Note:</strong> Both amounts shown above represent the same fund displayed in two different currencies. This is not two separate funds - it's your single available balance shown in USD and INR for your convenience.
                </Box>
              </Typography>
            </Paper>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} md={10} lg={8}>
              {/* Bank Details Card */}
              {showBank ? (
                <InfoCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <SectionTitle sx={{ mb: 0 }}>
                      <AccountBalanceIcon />
                      Bank Details
                    </SectionTitle>
                    <EditButton onClick={() => {
                      setTempBankDetails(bankDetails);
                      setAddBankModal(true);
                    }}>
                      <EditIcon />
                    </EditButton>
                  </Box>

                  <InfoRow>
                    <InfoLabel>
                      <AccountBalanceIcon sx={{ fontSize: 18 }} />
                      Bank Name <span style={{ color: '#d32f2f' }}>*</span>
                    </InfoLabel>
                    <InfoValue>{bank_name}</InfoValue>
                  </InfoRow>

                  <InfoRow>
                    <InfoLabel>
                      <TagIcon sx={{ fontSize: 18 }} />
                      IFSC Code <span style={{ color: '#d32f2f' }}>*</span>
                    </InfoLabel>
                    <InfoValue>{ifsc_code}</InfoValue>
                  </InfoRow>

                  <InfoRow>
                    <InfoLabel>
                      <LocationCityIcon sx={{ fontSize: 18 }} />
                      Branch Name <span style={{ color: '#d32f2f' }}>*</span>
                    </InfoLabel>
                    <InfoValue>{branch_name}</InfoValue>
                  </InfoRow>

                  <InfoRow>
                    <InfoLabel>
                      <CreditCardIcon sx={{ fontSize: 18 }} />
                      Account Number <span style={{ color: '#d32f2f' }}>*</span>
                    </InfoLabel>
                    <InfoValue>{account_number}</InfoValue>
                  </InfoRow>

                  <InfoRow>
                    <InfoLabel>
                      <BadgeIcon sx={{ fontSize: 18 }} />
                      PAN Number <span style={{ color: '#d32f2f' }}>*</span>
                    </InfoLabel>
                    <InfoValue>{pan}</InfoValue>
                  </InfoRow>

                  <InfoRow>
                    <InfoLabel>
                      <AccountCircleIcon sx={{ fontSize: 18 }} />
                      Holder Name <span style={{ color: '#d32f2f' }}>*</span>
                    </InfoLabel>
                    <InfoValue>{holder_name}</InfoValue>
                  </InfoRow>

                  <InfoRow>
                    <InfoLabel>
                      <DescriptionIcon sx={{ fontSize: 18 }} />
                      Other Details
                    </InfoLabel>
                    <InfoValue>{other_details || 'N/A'}</InfoValue>
                  </InfoRow>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Tooltip
                      title={!isBankDetailsFilled ? "Please fill all required bank details" : ""}
                      placement="top"
                    >
                      <span>
                        <ActionButton
                          onClick={() => setConfirmOpen(true)}
                          disabled={!isBankDetailsFilled}
                          startIcon={<AccountBalanceWalletIcon />}
                        >
                          Request Payout
                        </ActionButton>
                      </span>
                    </Tooltip>
                  </Box>
                </InfoCard>
              ) : (
                <InfoCard sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* Decorative Currency Background */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    opacity: 0.12,
                  }}>
                    <Typography sx={{
                      position: 'absolute',
                      fontSize: '120px',
                      fontWeight: 700,
                      color: '#1a73e8',
                      top: '-20px',
                      left: '-10px',
                      fontFamily: "'DM Sans', sans-serif",
                    }}>
                      $
                    </Typography>
                    <Typography sx={{
                      position: 'absolute',
                      fontSize: '80px',
                      fontWeight: 700,
                      color: '#00bfa5',
                      top: '40px',
                      right: '20px',
                      fontFamily: "'DM Sans', sans-serif",
                    }}>
                      ₹
                    </Typography>
                    <Typography sx={{
                      position: 'absolute',
                      fontSize: '100px',
                      fontWeight: 700,
                      color: '#1a73e8',
                      bottom: '-10px',
                      right: '-15px',
                      fontFamily: "'DM Sans', sans-serif",
                    }}>
                      $
                    </Typography>
                    <Typography sx={{
                      position: 'absolute',
                      fontSize: '70px',
                      fontWeight: 700,
                      color: '#00bfa5',
                      bottom: '30px',
                      left: '30px',
                      fontFamily: "'DM Sans', sans-serif",
                    }}>
                      ₹
                    </Typography>
                  </Box>

                  {/* Content */}
                  <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', py: 4 }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 64, color: '#1a73e8', mb: 2 }} />
                    <Typography sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#202124',
                      mb: 2,
                    }}>
                      Withdraw Your Earnings
                    </Typography>
                    <ActionButton
                      onClick={() => {
                        if (Number(revenue) < 100) {
                          return toastError("Minimum withdrawal amount is $100");
                        } else {
                          props.setLoader(true);
                          setTimeout(() => {
                            props.setLoader(false);
                            setShowBank(true);
                          }, 1000);
                        }
                      }}
                      startIcon={<AccountBalanceWalletIcon />}
                    >
                      Withdraw Funds
                    </ActionButton>
                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                      <Typography sx={{
                        fontSize: '0.75rem',
                        color: '#3c4043',
                        fontWeight: 500,
                        textAlign: 'center',
                        fontFamily: "'DM Sans', sans-serif",
                      }}>
                        Minimum withdrawal: $100
                      </Typography>
                    </Box>
                  </Box>
                </InfoCard>
              )}
            </Grid>
          </Grid>

          {/* Previously Requested Withdrawals */}
          <Box sx={{ mb: 2, mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#202124",
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <HistoryIcon sx={{ color: "#1a73e8" }} />
              Previously Requested Withdrawals
              <Typography
                component="span"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "#5f6368",
                  fontFamily: "'DM Sans', sans-serif",
                  fontStyle: "italic",
                }}
              >
                (showing history from September 1, 2025 onwards)
              </Typography>
            </Typography>
            {Array.isArray(payoutHistory) && payoutHistory.length > 0 && (
              <Button
                onClick={exportToPDF}
                startIcon={<DownloadIcon />}
                sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  padding: '10px 24px',
                  fontSize: '0.875rem',
                  boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1557b0 0%, #00a589 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(26, 115, 232, 0.4)',
                  },
                }}
              >
                Export to PDF
              </Button>
            )}
          </Box>

          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No.</TableCell>
                  <TableCell>Amount (USD)</TableCell>
                  <TableCell>Gross Amount (INR)</TableCell>
                  <TableCell>TDS Amount (INR)</TableCell>
                  <TableCell>Net Payable Amount (INR)</TableCell>
                  <TableCell>Requested On</TableCell>
                  <TableCell>UTR</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Transferred On</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(payoutHistory) && payoutHistory.length ? (
                  payoutHistory.map((item, key) => {
                    const statusType = item.status === "closed" ? "closed" : "open";
                    const statusLabel = item.status === "closed" ? "PAID" : "PENDING";
                    const statusIcon = item.status === "closed" ? <CheckCircleIcon /> : <PendingIcon />;

                    return (
                      <TableRow key={key}>
                        <TableCell>{key + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          $ {item.amount || "0.00"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {item.gross_amt ? `₹ ${item.gross_amt}` : "N/A"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {item.tds ? `₹ ${item.tds}` : "N/A"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          ₹ {item.email_amt || "0.00"}
                        </TableCell>
                        <TableCell>
                          {item.created_at ? moment(item.created_at).format("MMM DD, YYYY") : "N/A"}
                        </TableCell>
                        <TableCell>
                          {item.utr || "N/A"}
                        </TableCell>
                        <TableCell>
                          {item.ref || "N/A"}
                        </TableCell>
                        <TableCell>
                          {item.settled_on && item.settled_on !== "N/A"
                            ? moment(item.settled_on, "DD/MM/YYYY [at] HH:mm").format("MMM DD, YYYY")
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            statustype={statusType}
                            label={statusLabel}
                            icon={statusIcon}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography
                        sx={{ color: "#5f6368", fontStyle: "italic", py: 2 }}
                      >
                        No withdrawal history found. Request your first payout above.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {Array.isArray(payoutHistory) && payoutHistory.length > 0 && (
                <TableFooter>
                  <TableRow sx={{
                    backgroundColor: '#f5f5f5 !important',
                    '&:hover': {
                      backgroundColor: '#f5f5f5 !important',
                    },
                  }}>
                    <TableCell sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.9375rem',
                      color: '#202124',
                      borderBottom: 'none',
                    }}>
                      Total
                    </TableCell>
                    <TableCell sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.9375rem',
                      color: '#1a73e8',
                      borderBottom: 'none',
                    }}>
                      $ {payoutTotals.totalUSD.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.9375rem',
                      color: '#1a73e8',
                      borderBottom: 'none',
                    }}>
                      ₹ {payoutTotals.totalGrossINR.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.9375rem',
                      color: '#1a73e8',
                      borderBottom: 'none',
                    }}>
                      ₹ {payoutTotals.totalTDS.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.9375rem',
                      color: '#1a73e8',
                      borderBottom: 'none',
                    }}>
                      ₹ {payoutTotals.totalNetINR.toFixed(2)}
                    </TableCell>
                    <TableCell colSpan={5} sx={{ borderBottom: 'none' }} />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </StyledTableContainer>
        </Box>
      </Fade>

      {/* Bank Details Modal */}
      <StyledDialog
        open={addBankModal}
        onClose={() => setAddBankModal(false)}
        disableScrollLock
        maxWidth="sm"
      >
        <StyledDialogTitle>
          Update Bank Details
          <IconButton
            onClick={() => setAddBankModal(false)}
            sx={{
              color: '#5f6368',
              '&:hover': { backgroundColor: '#e0e0e0' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FieldLabel>
                <AccountBalanceIcon sx={{ fontSize: 16, mr: 0.5, color: '#1a73e8' }} />
                Bank Name <span style={{ color: '#d32f2f' }}>*</span>
              </FieldLabel>
              <StyledTextField
                placeholder="Enter bank name"
                variant="outlined"
                size="small"
                value={tempBankDetails.bank_name}
                onChange={(e) =>
                  setTempBankDetails({ ...tempBankDetails, bank_name: e.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel>
                <AccountCircleIcon sx={{ fontSize: 16, mr: 0.5, color: '#1a73e8' }} />
                Account Holder Name <span style={{ color: '#d32f2f' }}>*</span>
              </FieldLabel>
              <StyledTextField
                placeholder="Enter holder name"
                variant="outlined"
                size="small"
                value={tempBankDetails.holder_name}
                onChange={(e) =>
                  setTempBankDetails({
                    ...tempBankDetails,
                    holder_name: e.target.value,
                  })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel>
                <CreditCardIcon sx={{ fontSize: 16, mr: 0.5, color: '#1a73e8' }} />
                Account Number <span style={{ color: '#d32f2f' }}>*</span>
              </FieldLabel>
              <StyledTextField
                placeholder="Enter account number"
                variant="outlined"
                size="small"
                value={tempBankDetails.account_number}
                onChange={(e) =>
                  setTempBankDetails({
                    ...tempBankDetails,
                    account_number: e.target.value,
                  })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel>
                <TagIcon sx={{ fontSize: 16, mr: 0.5, color: '#1a73e8' }} />
                IFSC Code <span style={{ color: '#d32f2f' }}>*</span>
              </FieldLabel>
              <StyledTextField
                placeholder="Enter IFSC code"
                variant="outlined"
                size="small"
                value={tempBankDetails.ifsc_code}
                onChange={(e) =>
                  setTempBankDetails({ ...tempBankDetails, ifsc_code: e.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel>
                <LocationCityIcon sx={{ fontSize: 16, mr: 0.5, color: '#1a73e8' }} />
                Branch Name <span style={{ color: '#d32f2f' }}>*</span>
              </FieldLabel>
              <StyledTextField
                placeholder="Enter branch name"
                variant="outlined"
                size="small"
                value={tempBankDetails.branch_name}
                onChange={(e) =>
                  setTempBankDetails({
                    ...tempBankDetails,
                    branch_name: e.target.value,
                  })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel>
                <BadgeIcon sx={{ fontSize: 16, mr: 0.5, color: '#1a73e8' }} />
                PAN Number <span style={{ color: '#d32f2f' }}>*</span>
              </FieldLabel>
              <StyledTextField
                placeholder="Enter PAN number"
                variant="outlined"
                size="small"
                value={tempBankDetails.pan}
                onChange={(e) =>
                  setTempBankDetails({ ...tempBankDetails, pan: e.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel>
                <DescriptionIcon sx={{ fontSize: 16, mr: 0.5, color: '#1a73e8' }} />
                Other Details
              </FieldLabel>
              <StyledTextField
                placeholder="Additional information (optional)"
                variant="outlined"
                size="small"
                value={tempBankDetails.other_details}
                onChange={(e) =>
                  setTempBankDetails({
                    ...tempBankDetails,
                    other_details: e.target.value,
                  })
                }
                fullWidth
              />
            </Grid>
          </Grid>
        </StyledDialogContent>

        <StyledDialogActions>
          <Button
            onClick={() => setAddBankModal(false)}
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              color: '#5f6368',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            Cancel
          </Button>
          <ActionButton
            onClick={onClickSubmitBank}
            sx={{ boxShadow: 'none' }}
          >
            Save Details
          </ActionButton>
        </StyledDialogActions>
      </StyledDialog>

      {/* Payout Confirmation Modal */}
      <ConfirmationModal
        open={confirmOpen}
        title="Confirm Payout Request"
        message="Are you sure you want to request a payout? Your bank details will be verified before processing."
        onConfirm={requestForPayoutUser}
        onCancel={() => setConfirmOpen(false)}
        confirmText="Request Payout"
        cancelText="Cancel"
      />
    </PageContainer>
  );
}
